import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { sortBy } from '@shell/utils/sort';
import { NAMESPACE } from '@shell/config/types';
import { _CREATE, _EDIT } from '@shell/config/query-params';
import LabeledSelectWithCreate from '@shell/components/form/LabeledSelectWithCreate.vue';
import { useI18n } from '@shell/composables/useI18n';
import type { SelectOption } from '@shell/types/components/labeledSelect';
import type { Validator } from '@shell/utils/validators/formRules/index';

/**
 * A self-contained namespace selector that:
 *  - Reads namespaces from the Vuex store (or accepts overrides)
 *  - Prepends a "Create new namespace" option when the user has permission
 *  - Delegates the create/select UI to LabeledSelectWithCreate
 *  - Emits `update:value` (the namespace string) and `isNamespaceNew`
 */
interface RawNamespace {
  id?: string;
  name?: string;
  nameDisplay?: string;
  [key: string]: any;
}

interface NormalizedNamespace extends RawNamespace {
  id: string;
  nameDisplay: string;
}

type Mapper = (ns: NormalizedNamespace) => SelectOption;

interface Props {
  value?: string | null;
  mode?: string;
  label?: string;
  /** takes precedence over label if set */
  labelKey?: string | null;
  placeholder?: string;
  createPlaceholder?: string;
  disabled?: boolean;
  forceNamespace?: string | null;
  noDefaultNamespace?: boolean;
  override?: (string | RawNamespace)[] | null;
  options?: (string | RawNamespace)[] | null;
  mapper?: Mapper | null;
  createNamespaceOverride?: boolean;
  rules?: Validator[];
  name?: string;
  appendToBody?: boolean;
  requireDirty?: boolean;
  required?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'NamespaceSelect' },
  __name: 'NamespaceSelect',
  props: {
    value: { default: null },
    mode: { default: _CREATE },
    label: { default: undefined },
    labelKey: { default: 'nameNsDescription.namespace.label' },
    placeholder: { default: 'namespace.selectOrCreate' },
    createPlaceholder: { default: 'namespace.createNamespace' },
    disabled: { type: Boolean, default: false },
    forceNamespace: { default: null },
    noDefaultNamespace: { type: Boolean, default: false },
    override: { default: null },
    options: { default: null },
    mapper: { type: [Function, null], default: null },
    createNamespaceOverride: { type: Boolean, default: false },
    rules: { default: () => [] },
    name: { default: undefined },
    appendToBody: { type: Boolean, default: false },
    requireDirty: { type: Boolean, default: true },
    required: { type: Boolean, default: false }
  },
  emits: ["update:value", "isNamespaceNew"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const isCreate = computed(() => props.mode === _CREATE);

const allowedNamespaces = computed(() => store.getters.allowedNamespaces());
const storeNamespaces = computed(() => store.getters.namespaces());
const currentCluster = computed(() => store.getters.currentCluster);
const inStore = computed(() => store.getters['currentStore']());
const nsSchema = computed(() => store.getters[`${ inStore.value }/schemaFor`](NAMESPACE));

const canCreateNamespace = computed(() => {
  return (nsSchema.value?.collectionMethods || []).includes('POST') && currentCluster.value?.canUpdate;
});

const namespace = ref<string | null>(
  props.forceNamespace ??
    props.value ??
    (!props.noDefaultNamespace ? store.getters['defaultNamespace'] : null)
);

const realNamespaceOptions = computed<SelectOption[]>(() => {
  let namespaces: (string | RawNamespace)[];

  if (props.override) {
    namespaces = props.override;
  } else if (props.options) {
    namespaces = props.options.map((ns) => (typeof ns === 'string' ? ns : (ns.name || ns.id))).filter(Boolean).sort() as string[];
  } else {
    const namespaceObjs = isCreate.value ? allowedNamespaces.value : storeNamespaces.value;

    namespaces = Object.keys(namespaceObjs);
  }

  const normalized: NormalizedNamespace[] = namespaces
    .map((ns) => {
      if (typeof ns === 'string') {
        return { nameDisplay: ns, id: ns };
      }

      const id = ns?.id || ns?.name;
      const nameDisplay = ns?.nameDisplay || ns?.name || id;

      return {
        ...ns,
        nameDisplay,
        id,
      };
    })
    .filter((ns): ns is NormalizedNamespace => !!ns.id);

  const mapped = normalized
    .map(props.mapper || ((obj: NormalizedNamespace) => ({
      label: obj.nameDisplay,
      value: obj.id,
    })));

  const sorted = sortBy(mapped, 'label');

  if (props.forceNamespace) {
    sorted.unshift({ label: props.forceNamespace, value: props.forceNamespace });
  }

  return sorted;
});

const namespaceSelectOptions = computed<SelectOption[]>(() => {
  const sorted = [...realNamespaceOptions.value];

  // A namespace the user just created via the "create new" flow doesn't
  // exist in the store's namespace list yet - it isn't actually created
  // until save. Without a matching option, the underlying select has
  // nothing to display for the current value and falls back to blank.
  if (namespace.value && !sorted.some((o) => o.value === namespace.value)) {
    sorted.unshift({ label: namespace.value, value: namespace.value });
  }

  return sorted;
});

const lastIsNamespaceNew = ref<boolean | null>(null);

// Keep in sync when the parent changes value externally
watch(() => props.value, (val) => {
  if (val !== namespace.value) namespace.value = val ?? null;
});

const isNew = (val: string) => !val || !realNamespaceOptions.value.find((o) => o.value === val);

function emitNamespaceNewIfChanged(nextIsNew: boolean) {
  if (lastIsNamespaceNew.value !== nextIsNew) {
    lastIsNamespaceNew.value = nextIsNew;
    emit('isNamespaceNew', nextIsNew);
  }
}

function emitChange(val: string) {
  namespace.value = val;
  emit('update:value', val);
  emitNamespaceNewIfChanged(isNew(val));
}

function dispatchCreateNamespace(creating: boolean) {
  store.dispatch('cru-resource/setCreateNamespace', creating);
}
const isCreatingNamespace = ref(false);

function onUpdate(val: string) {
  if (!isCreatingNamespace.value) {
    dispatchCreateNamespace(false);
  }
  emitChange(val);
}

function onCreating() {
  isCreatingNamespace.value = true;
  dispatchCreateNamespace(true);
  emitNamespaceNewIfChanged(true);
}

function onCancel() {
  isCreatingNamespace.value = false;
  dispatchCreateNamespace(false);
  const defaultNs = store.getters['defaultNamespace'] || realNamespaceOptions.value.find((o) => !!o.value)?.value;

  emitChange(defaultNs);
}

const isReallyDisabled = computed(() => !!props.forceNamespace || props.disabled || props.mode === _EDIT);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LabeledSelectWithCreate, {
    value: namespace.value,
    name: __props.name,
    clearable: true,
    options: namespaceSelectOptions.value,
    disabled: isReallyDisabled.value,
    mode: __props.mode,
    label: __props.label,
    "label-key": __props.labelKey,
    placeholder: _unref(t)(__props.placeholder),
    "create-label": _unref(t)('namespace.createNamespace'),
    "create-placeholder": _unref(t)(__props.createPlaceholder),
    "create-allowed": canCreateNamespace.value || __props.createNamespaceOverride,
    rules: __props.rules,
    "append-to-body": __props.appendToBody,
    "require-dirty": __props.requireDirty,
    required: __props.required,
    "onUpdate:value": onUpdate,
    onCreating: onCreating,
    onCancel: onCancel
  }, null, 8, ["value", "name", "options", "disabled", "mode", "label", "label-key", "placeholder", "create-label", "create-placeholder", "create-allowed", "rules", "append-to-body", "require-dirty", "required"]))
}
}

})